import { useEffect, useState } from "react";
import { readSessionUser } from "../../../lib/authSession";
import { DEFAULT_SITE_CONTENT, type SiteContentState } from "../../../lib/siteContentStore";
import { useAdminSiteContent } from "../../../hooks/useSiteContent";

function Field({
  label,
  value,
  onChange,
  placeholder,
  textarea,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  textarea?: boolean;
}) {
  return (
    <div>
      <label className="mb-1 block text-[10px] font-semibold uppercase tracking-wide text-farm-muted">
        {label}
      </label>
      {textarea ? (
        <textarea
          value={value}
          onChange={(event) => onChange(event.target.value)}
          placeholder={placeholder}
          rows={4}
          className="w-full resize-none rounded-xl border border-farm-border bg-farm-cream px-3 py-2 text-sm text-farm-brown outline-none focus:border-farm-earth"
        />
      ) : (
        <input
          value={value}
          onChange={(event) => onChange(event.target.value)}
          placeholder={placeholder}
          className="h-10 w-full rounded-xl border border-farm-border bg-farm-cream px-3 text-sm text-farm-brown outline-none focus:border-farm-earth"
        />
      )}
    </div>
  );
}

function Toggle({
  label,
  checked,
  onChange,
}: {
  label: string;
  checked: boolean;
  onChange: (value: boolean) => void;
}) {
  return (
    <button
      type="button"
      onClick={() => onChange(!checked)}
      className={`inline-flex h-9 items-center gap-2 rounded-full border px-3 text-xs font-semibold ${
        checked
          ? "border-farm-earth bg-farm-earth text-white"
          : "border-farm-border bg-white text-farm-muted"
      }`}
    >
      <span className={`h-2 w-2 rounded-full ${checked ? "bg-white" : "bg-farm-muted"}`} />
      {label}: {checked ? "Açık" : "Kapalı"}
    </button>
  );
}

export default function SiteYonetimiPage() {
  const sessionUser = readSessionUser();
  const { content, saveSiteContent, rehydrateSiteContentAdmin } = useAdminSiteContent();
  const [draft, setDraft] = useState<SiteContentState>(content);
  const [message, setMessage] = useState("");

  useEffect(() => {
    setDraft(content);
  }, [content]);

  const canAccess = sessionUser?.rol === "Admin";

  function patch<K extends keyof SiteContentState>(key: K, value: SiteContentState[K]) {
    setDraft((prev) => ({ ...prev, [key]: value }));
  }

  function patchHero(key: keyof SiteContentState["hero"], value: string) {
    setDraft((prev) => ({ ...prev, hero: { ...prev.hero, [key]: value } }));
  }

  function patchAnnouncement<K extends keyof SiteContentState["announcement"]>(
    key: K,
    value: SiteContentState["announcement"][K]
  ) {
    setDraft((prev) => ({
      ...prev,
      announcement: { ...prev.announcement, [key]: value },
    }));
  }

  function patchAbout(key: keyof SiteContentState["about"], value: string) {
    setDraft((prev) => ({ ...prev, about: { ...prev.about, [key]: value } }));
  }

  function patchSeo(key: keyof SiteContentState["seo"], value: string) {
    setDraft((prev) => ({ ...prev, seo: { ...prev.seo, [key]: value } }));
  }

  function patchService(index: number, patch: Partial<SiteContentState["services"][number]>) {
    setDraft((prev) => ({
      ...prev,
      services: prev.services.map((item, idx) =>
        idx === index ? { ...item, ...patch } : item
      ),
    }));
  }

  function addService() {
    setDraft((prev) => ({
      ...prev,
      services: [
        ...prev.services,
        {
          id: `hizmet-${Date.now()}`,
          title: "Yeni Hizmet",
          description: "Hizmet açıklaması",
          icon: "ri-leaf-line",
          active: true,
        },
      ],
    }));
  }

  function removeService(index: number) {
    setDraft((prev) => ({
      ...prev,
      services: prev.services.filter((_, idx) => idx !== index),
    }));
  }

  async function handleSave() {
    try {
      await saveSiteContent(draft);
      setMessage("Site içerikleri kaydedildi. Ana sayfada ve hizmetlerde kullanılacak.");
      window.setTimeout(() => setMessage(""), 3500);
    } catch (error) {
      console.error(error);
      window.alert("Site içerikleri kaydedilemedi. Lütfen tekrar deneyin.");
    }
  }

  async function handleReload() {
    await rehydrateSiteContentAdmin();
    setMessage("Son kayıt tekrar yüklendi.");
    window.setTimeout(() => setMessage(""), 2500);
  }

  if (!canAccess) {
    return (
      <div className="min-h-screen bg-farm-cream px-4 py-8">
        <div className="mx-auto max-w-md rounded-farm border border-farm-border bg-white p-6 text-center">
          <i className="ri-lock-line text-3xl text-red-500" />
          <p className="mt-2 font-heading text-base font-bold text-farm-brown">
            Yetkiniz yok
          </p>
          <p className="mt-1 text-xs text-farm-muted">
            Site Yönetimi sadece Admin hesabında açıktır.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-farm-cream font-body">
      <div className="mx-auto max-w-7xl space-y-4 px-3 py-4 sm:px-5">
        <div className="rounded-farm border border-farm-border bg-white px-4 py-3">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <h1 className="font-heading text-base font-bold text-farm-brown">
                Site Yönetimi
              </h1>
              <p className="mt-1 text-xs text-farm-muted">
                Web sitesinin görünen yüzündeki metinleri, telefonları, hizmetleri ve duyuru bandını blok blok düzenle.
              </p>
            </div>
            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                onClick={handleReload}
                className="h-9 rounded-xl border border-farm-border bg-white px-3 text-xs font-semibold text-farm-brown hover:bg-farm-cream"
              >
                Son Kaydı Yükle
              </button>
              <button
                type="button"
                onClick={() => setDraft(DEFAULT_SITE_CONTENT)}
                className="h-9 rounded-xl border border-farm-border bg-white px-3 text-xs font-semibold text-farm-muted hover:bg-farm-cream"
              >
                Varsayılana Dön
              </button>
              <button
                type="button"
                onClick={handleSave}
                className="h-9 rounded-xl bg-farm-earth px-4 text-xs font-semibold text-white hover:bg-farm-brown"
              >
                Kaydet
              </button>
            </div>
          </div>
          {message && (
            <div className="mt-3 rounded-xl border border-green-200 bg-green-50 px-3 py-2 text-xs text-green-700">
              {message}
            </div>
          )}
        </div>

        <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_360px]">
          <div className="space-y-4">
            <section className="rounded-farm border border-farm-border bg-white p-4">
              <h2 className="font-heading text-sm font-bold text-farm-brown">Genel İletişim</h2>
              <div className="mt-3 grid gap-3 md:grid-cols-2">
                <Field label="Marka Adı" value={draft.brandName} onChange={(v) => patch("brandName", v)} />
                <Field label="Telefon" value={draft.contactPhone} onChange={(v) => patch("contactPhone", v)} />
                <Field label="WhatsApp No" value={draft.contactWhatsapp} onChange={(v) => patch("contactWhatsapp", v)} placeholder="905357230528" />
                <Field label="E-posta" value={draft.contactEmail} onChange={(v) => patch("contactEmail", v)} />
                <Field label="Harita Linki" value={draft.mapsUrl} onChange={(v) => patch("mapsUrl", v)} />
                <Field label="Adres" value={draft.address} onChange={(v) => patch("address", v)} textarea />
              </div>
            </section>

            <section className="rounded-farm border border-farm-border bg-white p-4">
              <h2 className="font-heading text-sm font-bold text-farm-brown">Ana Sayfa Hero</h2>
              <div className="mt-3 grid gap-3 md:grid-cols-2">
                <Field label="Üst Rozet" value={draft.hero.eyebrow} onChange={(v) => patchHero("eyebrow", v)} />
                <Field label="Ana Başlık" value={draft.hero.title} onChange={(v) => patchHero("title", v)} />
                <Field label="Alt Başlık" value={draft.hero.subtitle} onChange={(v) => patchHero("subtitle", v)} />
                <Field label="Ana Görsel URL" value={draft.hero.imageUrl} onChange={(v) => patchHero("imageUrl", v)} />
                <Field label="Buton Yazısı" value={draft.hero.primaryButtonText} onChange={(v) => patchHero("primaryButtonText", v)} />
                <Field label="Buton Linki" value={draft.hero.primaryButtonLink} onChange={(v) => patchHero("primaryButtonLink", v)} />
                <Field label="WhatsApp Buton Yazısı" value={draft.hero.secondaryButtonText} onChange={(v) => patchHero("secondaryButtonText", v)} />
                <Field label="Açıklama" value={draft.hero.description} onChange={(v) => patchHero("description", v)} textarea />
              </div>
            </section>

            <section className="rounded-farm border border-farm-border bg-white p-4">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <h2 className="font-heading text-sm font-bold text-farm-brown">Duyuru / Reklam Bandı</h2>
                <div className="flex gap-2">
                  <Toggle label="Aktif" checked={draft.announcement.enabled} onChange={(v) => patchAnnouncement("enabled", v)} />
                  <Toggle label="Kayan" checked={draft.announcement.marquee} onChange={(v) => patchAnnouncement("marquee", v)} />
                </div>
              </div>
              <div className="mt-3 grid gap-3 md:grid-cols-2">
                <Field label="Duyuru Metni" value={draft.announcement.text} onChange={(v) => patchAnnouncement("text", v)} />
                <Field label="Buton Yazısı" value={draft.announcement.buttonText} onChange={(v) => patchAnnouncement("buttonText", v)} />
                <Field label="Buton Linki" value={draft.announcement.buttonLink} onChange={(v) => patchAnnouncement("buttonLink", v)} />
              </div>
            </section>

            <section className="rounded-farm border border-farm-border bg-white p-4">
              <div className="flex items-center justify-between gap-2">
                <h2 className="font-heading text-sm font-bold text-farm-brown">Hizmet Kartları</h2>
                <button type="button" onClick={addService} className="h-8 rounded-xl bg-farm-earth px-3 text-xs font-semibold text-white">
                  Hizmet Ekle
                </button>
              </div>
              <div className="mt-3 space-y-3">
                {draft.services.map((service, index) => (
                  <div key={service.id} className="rounded-xl border border-farm-border bg-farm-warm p-3">
                    <div className="mb-2 flex items-center justify-between gap-2">
                      <Toggle label="Göster" checked={service.active} onChange={(v) => patchService(index, { active: v })} />
                      <button type="button" onClick={() => removeService(index)} className="text-xs font-semibold text-red-600">
                        Sil
                      </button>
                    </div>
                    <div className="grid gap-2 md:grid-cols-2">
                      <Field label="Başlık" value={service.title} onChange={(v) => patchService(index, { title: v })} />
                      <Field label="İkon" value={service.icon ?? ""} onChange={(v) => patchService(index, { icon: v })} placeholder="ri-seedling-line" />
                      <Field label="Görsel URL" value={service.imageUrl ?? ""} onChange={(v) => patchService(index, { imageUrl: v })} />
                      <Field label="Açıklama" value={service.description} onChange={(v) => patchService(index, { description: v })} textarea />
                    </div>
                  </div>
                ))}
              </div>
            </section>

            <section className="rounded-farm border border-farm-border bg-white p-4">
              <h2 className="font-heading text-sm font-bold text-farm-brown">Hakkımızda ve SEO</h2>
              <div className="mt-3 grid gap-3 md:grid-cols-2">
                <Field label="Hakkımızda Başlık" value={draft.about.title} onChange={(v) => patchAbout("title", v)} />
                <Field label="Hakkımızda Görsel URL" value={draft.about.imageUrl ?? ""} onChange={(v) => patchAbout("imageUrl", v)} />
                <Field label="Hakkımızda Açıklama" value={draft.about.description} onChange={(v) => patchAbout("description", v)} textarea />
                <Field label="Ana Sayfa SEO Başlık" value={draft.seo.homeTitle} onChange={(v) => patchSeo("homeTitle", v)} />
                <Field label="Ana Sayfa SEO Açıklama" value={draft.seo.homeDescription} onChange={(v) => patchSeo("homeDescription", v)} textarea />
                <Field label="Hizmetler SEO Başlık" value={draft.seo.servicesTitle} onChange={(v) => patchSeo("servicesTitle", v)} />
              </div>
            </section>
          </div>

          <aside className="rounded-farm border border-farm-border bg-white p-4 lg:sticky lg:top-4 lg:self-start">
            <p className="text-[10px] font-semibold uppercase tracking-wide text-farm-muted">Canlı Önizleme</p>
            <div className="mt-3 overflow-hidden rounded-2xl border border-farm-border bg-farm-cream">
              <div className="relative h-44">
                <img src={draft.hero.imageUrl} alt="" className="h-full w-full object-cover" />
                <div className="absolute inset-0 bg-black/35" />
                <div className="absolute inset-0 flex flex-col justify-end p-4 text-white">
                  <span className="text-[10px] font-semibold">{draft.hero.eyebrow}</span>
                  <strong className="mt-1 text-xl">{draft.hero.title}</strong>
                  <span className="text-xs text-farm-gold-light">{draft.hero.subtitle}</span>
                </div>
              </div>
              <div className="p-3">
                {draft.announcement.enabled && (
                  <div className="mb-3 rounded-xl bg-farm-gold px-3 py-2 text-xs font-semibold text-white">
                    {draft.announcement.text}
                  </div>
                )}
                <p className="text-xs text-farm-muted">{draft.hero.description}</p>
                <div className="mt-3 rounded-xl border border-farm-border bg-white p-3 text-xs">
                  <p className="font-semibold text-farm-brown">İletişim</p>
                  <p className="mt-1 text-farm-muted">{draft.contactPhone}</p>
                  <p className="text-farm-muted whitespace-pre-line">{draft.address}</p>
                </div>
              </div>
            </div>
            <p className="mt-3 text-[11px] leading-relaxed text-farm-muted">
              Not: İlk sürümde görseller URL ile yönetilir. Bilgisayardan dosya yükleme istenirse ayrı güvenli upload paketi yapılabilir.
            </p>
          </aside>
        </div>
      </div>
    </div>
  );
}
